"use client";

/**
 * "You have opened five listings - want the new ones?"
 *
 * Saved searches run at about nine a month while the browse page is the
 * busiest surface in the product, so the alert has to be offered where
 * the intent already is (owner, 2026-09-21). It appears once per browser
 * session, after the fifth listing drawer, and it is a row above the
 * grid, never a modal: the visitor is mid-task and an overlay would be
 * the exact interruption the scroll CTA was removed for.
 *
 * It carries the filters the person is actually browsing with, through
 * the same prefill the toolbar's bell uses, so the alert form opens
 * filled in. It stays away when nothing is filtered (an alert on the
 * whole market is refused by the form anyway), when an identical alert
 * already exists, and for the rest of the session once dismissed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { BellPlus, X } from "lucide-react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { API_BASE } from "@/lib/api";
import { alertFilterKey, browseParamsToAlertFilters, encodePrefill } from "@/lib/filter-presets";

const OPENS_KEY = "menzil.listings.opens";
// localStorage, not sessionStorage: dismissed once means dismissed for
// good (owner, 2026-09-22: "if I close it once, I don't want to see it
// again"). The counter stays per session; the refusal does not.
const DISMISS_KEY = "menzil.listings.opens.dismissed";
/** Fifth drawer: enough to read as a search, few enough to still matter. */
export const NUDGE_AFTER_OPENS = 5;

/** Count one drawer open. Exported so the grid can call it without
 *  knowing where the counter lives. */
export function countListingOpen(): number {
  try {
    const n = Number(sessionStorage.getItem(OPENS_KEY) ?? "0") + 1;
    sessionStorage.setItem(OPENS_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

export default function DealAlertNudge({
  searchParams,
  opens,
}: {
  searchParams: ReadonlyURLSearchParams;
  /** Drawer opens counted this session (the grid owns the counter). */
  opens: number;
}) {
  const t = useT();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true);
  // The account's alert filter keys, read once per mount (a filter change
  // used to re-download the whole /alerts list); remounting after an
  // alert was created elsewhere reads them fresh.
  const [alertKeys, setAlertKeys] = useState<Set<string> | null>(null);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1"
        || sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  const blob = useMemo(
    () => browseParamsToAlertFilters(searchParams),
    [searchParams],
  );
  const filtered = Object.keys(blob).length > 0;
  const due = opens >= NUDGE_AFTER_OPENS && filtered && !dismissed;

  // Only once the row is about to appear, and only for signed-in
  // visitors: an anonymous one has nothing to collide with, and the
  // browse page should not pay for a request nobody will read.
  const uid = user?.id ?? null;
  const loadKeys = due && !!uid;
  const keysFor = useRef<string | null>(null);
  useEffect(() => {
    if (!loadKeys || keysFor.current === uid) return;
    keysFor.current = uid;
    let cancelled = false;
    let done = false;
    void fetch(`${API_BASE}/alerts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        done = true;
        if (cancelled) return;
        setAlertKeys(new Set((d.items || []).map(
          (a: { filters?: Record<string, unknown> }) => alertFilterKey(a.filters || {}),
        )));
      })
      .catch(() => { /* an alert we cannot check for is an alert we offer */ });
    return () => {
      cancelled = true;
      if (!done) keysFor.current = null; // interrupted: read again next time
    };
  }, [loadKeys, uid]);
  const dup = useMemo(() => !!alertKeys?.has(alertFilterKey(blob)), [alertKeys, blob]);

  if (!due || dup) return null;

  return (
    <div className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
      {/* Gray surface, not indigo: `bg-indigo-50` is outside the gray
          token swap, so in dark mode it stayed a bright lavender panel
          (owner, 2026-09-22: "the border colour is not suitable for dark
          mode, it's too bright"). */}
      <span className="flex min-w-0 items-center gap-2 text-[13px] text-gray-700">
        <BellPlus className="h-4 w-4 shrink-0 text-gray-400" />
        <span className="truncate">{t("listings.opens.nudge.short")}</span>
      </span>
      <span className="flex shrink-0 items-center gap-0.5">
        <Link
          href={`/alerts/new?prefill=${encodePrefill(blob)}`}
          className="rounded-lg px-2 py-1 text-[13px] font-bold text-gray-900 transition-colors hover:bg-gray-100"
        >
          {t("listings.opens.nudge.cta")}
        </Link>
        <button
          type="button"
          aria-label={t("listings.opens.nudge.close")}
          onClick={() => {
            setDismissed(true);
            try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
          }}
          className="rounded-lg p-1 text-gray-400 transition-colors hover:text-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </span>
    </div>
  );
}
